import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { buildsApi } from '../../api/builds';
import type { Build } from '../../api/builds';
import { Layout } from '../../components/Layout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import {
  ArrowLeft,
  Download,
  Trash2,
  CheckCircle,
  Clock,
  XCircle,
  RefreshCw,
  FileCode,
  Shield,
  Fingerprint,
  Calendar,
  Activity,
  Lock,
  Copy,
  Check,
} from 'lucide-react';

export function BuildDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [build, setBuild] = useState<Build | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);

  const copyPassword = async () => {
    if (build?.zipPassword) {
      await navigator.clipboard.writeText(build.zipPassword);
      setCopiedPassword(true);
      setTimeout(() => setCopiedPassword(false), 2000);
    }
  };

  useEffect(() => {
    if (id) {
      loadBuild();
    }
  }, [id]);

  // Poll for status if processing
  useEffect(() => {
    if (build?.status === 'PROCESSING' || build?.status === 'PENDING') {
      const interval = setInterval(loadBuild, 2000);
      return () => clearInterval(interval);
    }
  }, [build?.status]);

  const loadBuild = async () => {
    if (!id) return;
    
    try {
      const data = await buildsApi.getStatus(id);
      setBuild(data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load build');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !confirm('Are you sure you want to delete this build?')) return;
    
    setDeleting(true);
    try {
      await buildsApi.delete(id);
      navigate('/builds');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete build');
      setDeleting(false);
    }
  };

  const getStatusConfig = (status: string) => {
    const configs: Record<string, { icon: React.ReactNode; class: string; label: string; color: string }> = {
      COMPLETED: {
        icon: <CheckCircle className="w-6 h-6" />,
        class: 'badge-completed',
        label: 'Completed',
        color: 'text-cyan-400',
      },
      PROCESSING: {
        icon: <RefreshCw className="w-6 h-6 animate-spin" />,
        class: 'badge-processing',
        label: 'Processing',
        color: 'text-blue-400',
      },
      PENDING: {
        icon: <Clock className="w-6 h-6" />,
        class: 'badge-pending',
        label: 'Pending',
        color: 'text-yellow-400',
      },
      FAILED: {
        icon: <XCircle className="w-6 h-6" />,
        class: 'badge-failed',
        label: 'Failed',
        color: 'text-red-400',
      },
      EXPIRED: {
        icon: <Clock className="w-6 h-6" />,
        class: 'bg-slate-700/50 text-slate-400 border border-slate-600/50',
        label: 'Expired',
        color: 'text-slate-400',
      },
    };
    return configs[status] || configs.PENDING;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <div className="relative w-12 h-12">
            <div className="absolute inset-0 rounded-full border-2 border-slate-800" />
            <div className="absolute inset-0 rounded-full border-2 border-cyan-500 border-t-transparent animate-spin" />
          </div>
        </div>
      </Layout>
    );
  }

  if (error && !build) {
    return (
      <Layout>
        <div className="text-center py-20">
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Build Not Found</h2>
          <p className="text-slate-400 mb-6">{error}</p>
          <Link to="/builds">
            <Button variant="secondary">
              <ArrowLeft className="w-4 h-4" />
              Back to Builds
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  if (!build) return null;

  const statusConfig = getStatusConfig(build.status);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between opacity-0-init animate-fade-in-up">
          <div>
            <Link 
              to="/builds" 
              className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Builds
            </Link>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <FileCode className="w-8 h-8 text-cyan-400" />
              {build.filename}
            </h1>
          </div>
          <div className="flex gap-3">
            {build.status === 'COMPLETED' && build.downloadUrl && (
              <a href={buildsApi.download(build.id)}>
                <Button glow>
                  <Download className="w-4 h-4" />
                  Download
                </Button>
              </a>
            )}
            <Button variant="ghost" onClick={handleDelete} disabled={deleting}>
              <Trash2 className="w-4 h-4" />
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </div>

        {/* Status Card */}
        <Card variant="bordered" className="opacity-0-init animate-fade-in-up animation-delay-100">
          <div className="flex items-center gap-6">
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
              build.status === 'COMPLETED' ? 'bg-cyan-500/10 border border-cyan-500/20' :
              build.status === 'FAILED' ? 'bg-red-500/10 border border-red-500/20' :
              build.status === 'PROCESSING' ? 'bg-blue-500/10 border border-blue-500/20' :
              'bg-slate-700/50 border border-slate-600/50'
            }`}>
              <span className={statusConfig.color}>{statusConfig.icon}</span>
            </div>
            <div className="flex-1">
              <span className={`badge ${statusConfig.class} mb-2`}>
                {statusConfig.label}
              </span>
              <p className="text-slate-400 text-sm">
                {build.status === 'COMPLETED' && 'Your protected binary is ready for download'}
                {build.status === 'PROCESSING' && 'Your binary is being protected...'}
                {build.status === 'PENDING' && 'Build is queued for processing'}
                {build.status === 'FAILED' && 'Build failed - see error details below'}
                {build.status === 'EXPIRED' && 'This build has expired and files have been deleted'}
              </p>
            </div>
          </div>
        </Card>

        {/* Error Message */}
        {build.status === 'FAILED' && build.error && (
          <Card className="opacity-0-init animate-fade-in-up animation-delay-200 border-red-500/20">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                <XCircle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h3 className="text-red-400 font-semibold mb-1">Error</h3>
                <p className="text-slate-300">{build.error}</p>
              </div>
            </div>
          </Card>
        )}

        {/* Console Output */}
        {build.consoleOutput && (
          <Card title="Console Output" className="opacity-0-init animate-fade-in-up animation-delay-300">
            <pre className="bg-slate-900/50 rounded-xl p-4 text-sm font-mono text-slate-300 overflow-x-auto whitespace-pre-wrap">
              {build.consoleOutput}
            </pre>
          </Card>
        )}

        {/* Build Details */}
        <Card title="Build Details" className="opacity-0-init animate-fade-in-up animation-delay-300">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-slate-500 text-xs uppercase tracking-wider">Engine</p>
                  <p className="text-white font-medium">
                    {build.engineType}
                    {build.engineVersion && <span className="text-slate-500 ml-1">v{build.engineVersion}</span>}
                  </p>
                </div>
              </div>

              {build.watermark && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                    <Fingerprint className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs uppercase tracking-wider">Watermark</p>
                    <code className="text-cyan-400 font-mono">{build.watermark}</code>
                  </div>
                </div>
              )}

              {build.zipPassword && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                    <Lock className="w-5 h-5 text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-slate-500 text-xs uppercase tracking-wider">ZIP Password</p>
                    <div className="flex items-center gap-2">
                      <code className="text-amber-400 font-mono">{build.zipPassword}</code>
                      <button
                        onClick={copyPassword}
                        className="p-1.5 rounded-lg hover:bg-slate-700/50 text-slate-500 hover:text-white transition-all"
                        title="Copy password"
                      >
                        {copiedPassword ? (
                          <Check className="w-4 h-4 text-green-400" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-700/50 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-slate-400" />
                </div>
                <div>
                  <p className="text-slate-500 text-xs uppercase tracking-wider">Created</p>
                  <p className="text-white">{new Date(build.createdAt).toLocaleString()}</p>
                </div>
              </div>

              {build.completedAt && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-slate-700/50 flex items-center justify-center">
                    <Activity className="w-5 h-5 text-slate-400" />
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs uppercase tracking-wider">Completed</p>
                    <p className="text-white">{new Date(build.completedAt).toLocaleString()}</p>
                  </div>
                </div>
              )}

              {build.expiresAt && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-slate-700/50 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-slate-400" />
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs uppercase tracking-wider">Expires</p>
                    <p className="text-white">{new Date(build.expiresAt).toLocaleString()}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>
    </Layout>
  );
}

